import React from 'react';
import { ExternalLink, Play } from 'lucide-react';

/**
 * BLOCK: media_card
 * ACF layout name: `block_media_card`
 *
 * Fields:
 *  - layout       (select: wide | panel)
 *  - title        (text)
 *  - description  (textarea, optional)
 *  - eyebrow      (text, optional)
 *  - thumbnail    (image)
 *  - url          (url — YouTube / Spotify / external)
 *  - link_label   (text)
 *
 * Consolidates the YouTube cards on Guided Meditations and Creative Corner,
 * plus the Spotify panel on Podcasts and the episode card on Meet Milangela.
 */

interface MediaCardProps {
  layout?: 'wide' | 'panel';
  eyebrow?: string;
  title: string;
  description?: string;
  thumbnail: string;
  thumbnailAlt: string;
  url: string;
  linkLabel?: string;
  className?: string;
}
export function MediaCard({
  layout = 'wide',
  eyebrow,
  title,
  description,
  thumbnail,
  thumbnailAlt,
  url,
  linkLabel = 'Watch now',
  className = ''
}: MediaCardProps) {
  if (layout === 'panel') {
    return <a href={url} target="_blank" rel="noopener noreferrer" aria-label={`${linkLabel}: ${title} (opens in a new tab)`} className={`group grid overflow-hidden border border-charcoal/10 bg-charcoal text-cream shadow-soft transition-colors hover:bg-charcoal-900 focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 md:grid-cols-[minmax(230px,0.8fr)_1.2fr] ${className}`} data-id="element-108" data-mp="1sojqexsbyuj3">
        <div className="relative aspect-square overflow-hidden bg-sage-700 md:aspect-auto" data-id="element-109" data-mp="191m2eoo59yb8">
          <img src={thumbnail} alt={thumbnailAlt} loading="lazy" decoding="async" width={640} height={640} className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105" data-id="element-110" data-mp="150im6126q09h" />
          <div aria-hidden="true" className="absolute inset-0 bg-charcoal/25" data-id="element-111" data-mp="q8qhcj00o4m0" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-112" data-mp="la6wklx5syv8">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-cream/70 bg-cream/90 text-charcoal shadow-lg transition-transform duration-300 group-hover:scale-110" data-id="element-113" data-mp="2ldzoe4lefki2">
              <Play size={25} fill="currentColor" className="ml-1" aria-hidden="true" data-id="element-114" data-mp="2v2rcu2r93mo8" />
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-between p-7 sm:p-10" data-id="element-115" data-mp="301uhvn78dsw7">
          <div data-id="element-116" data-mp="22ff0elet0v8n">
            {eyebrow && <span className="text-[11px] font-medium tracking-[0.22em] text-rose uppercase" data-id="element-117" data-mp="22gq28foovsx4">
                {eyebrow}
              </span>}
            <h3 className="mt-6 font-serif text-3xl leading-tight text-cream sm:text-4xl" data-id="element-118" data-mp="2ccjlehwwyknv">
              {title}
            </h3>
            {description && <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-cream-100/75" data-id="element-119" data-mp="2gc1zatqbhvh7">
                {description}
              </p>}
          </div>
          <span className="mt-10 inline-flex items-center gap-3 self-start border-b border-cream/60 pb-1 text-xs font-medium tracking-[0.16em] text-cream uppercase transition-colors group-hover:border-rose group-hover:text-rose" data-id="element-120" data-mp="3rrql3hru4ogf">
            {linkLabel}
            <ExternalLink size={15} strokeWidth={1.5} aria-hidden="true" data-id="element-121" data-mp="17d38dx5ok7zz" />
          </span>
        </div>
      </a>;
  }
  return <a href={url} target="_blank" rel="noopener noreferrer" aria-label={`${linkLabel}: ${title} (opens in a new tab)`} className={`group relative block aspect-video overflow-hidden rounded-2xl bg-charcoal shadow-soft focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4 ${className}`} data-id="element-122" data-mp="12ea48vca9tch">
      <img src={thumbnail} alt={thumbnailAlt} loading="lazy" decoding="async" width={1280} height={720} className="h-full w-full object-cover opacity-90 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100" data-id="element-123" data-mp="jfxlchvkl53p" />
      <div className="absolute inset-0 flex items-center justify-center bg-charcoal/30" data-id="element-124" data-mp="1aj1gbonmysi2">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110" data-id="element-125" data-mp="1ah87c2pjvw6w">
          <Play size={26} fill="#3A3A38" className="ml-1 text-charcoal" aria-hidden="true" data-id="element-126" data-mp="11hbd7lke654g" />
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5" data-id="element-127" data-mp="16g4vdramikay">
        <p className="text-sm font-medium tracking-wide text-white drop-shadow" data-id="element-128" data-mp="rr0a218j7zym">
          {title}
        </p>
      </div>
    </a>;
}